import { CONNECT_FAIL_MESSAGE } from '@mm/state/actions/facebookActions';
import { ResponseOrError } from '@mm/state/errorHandlers/common-handler';
import { onGlobalVarSet } from '@mm/utils';
import * as Sentry from '@sentry/react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { FullStory } from '../FullStory';
import RedactHelper from './redact-helper';
import { shouldRateLimitEvent } from './should-rate-limit-event';

const ignoreErrors = [
  /vid_mate_check is not defined/,
  /There is not enough space on the disk/,

  // JSON.parse errors caused by facebook SDK in private browser sessions
  /JSON Parse error: Unexpected identifier .*/, // iOS error message
  /Unexpected token .* in JSON at position .*/, // Chrome Mobile error message
];

const sentryBeforeSend = async (event: Sentry.Event) => {
  // Returning null stops the event from being sent
  if (shouldRateLimitEvent(event)) {
    return null;
  }

  try {
    const extra = event.extra
      ? RedactHelper.redactObjectValues(event.extra)
      : {};
    const FullStory = await onGlobalVarSet<FullStory>('FS', {
      maxPollNumber: 5,
    });

    if (FullStory.getCurrentSessionURL) {
      extra.fullstorySession = FullStory.getCurrentSessionURL(true);
    }
    event.extra = extra;
  } catch (e) {}

  return event;
};

export const initialize = (
  dsn: string,
  extraOptions?: {
    attachStacktrace?: boolean;
  },
) => {
  if (IS_PROD) {
    Sentry.init({
      dsn,
      enabled: !!IS_PROD,
      beforeSend: sentryBeforeSend,
      ignoreErrors,
      release: SENTRY_RELEASE,
      ...(extraOptions || {}),
    });
  }
};

export const setUserContext = (id: number, email: string) => {
  Sentry.configureScope(scope => {
    scope.setUser({ email, id: String(id) });
  });
};

export const captureException = (
  error: ResponseOrError,
  errorOptions: ErrorReportOptions = {},
) => {
  const { extraData, fingerprints, tags, logResponseErrors } = errorOptions;

  // Only send if standard error not response failure
  if (error && (logResponseErrors || !(error instanceof Response))) {
    if (!IS_PROD) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    return Sentry.captureException(error, {
      extra: extraData,
      fingerprint: fingerprints,
      tags,
    });
  }
};

type ErrorReportOptions = {
  extraData?: Record<string, any>;
  fingerprints?: string[];
  tags?: Record<string, any>;
  logResponseErrors?: boolean;
};

export const reportError = (
  errorMessage: string,
  errorOptions: ErrorReportOptions = {},
) => {
  const { extraData, fingerprints, tags } = errorOptions;

  // Improve ignore logic in future
  if (errorMessage === CONNECT_FAIL_MESSAGE) {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(errorMessage, extraData);

  return Sentry.captureMessage(errorMessage, {
    extra: extraData,
    fingerprint: fingerprints,
    tags,
  });
};

export const { addBreadcrumb } = Sentry;
